/**
 * CAD-workspace parity runners — commands that previously activated BIM
 * class tools which are NOT mounted in the 2D viewport, so typing them
 * in the CAD command line did nothing (docs/plans/2d/
 * 2d-full-parity-plan.md, Faz 0.1). Each runner produces real drafting
 * entities in the shared CAD ledger instead.
 *
 * Covered: HATCH / REGION / REVCLOUD / ARRAYRECT / ARRAYPOLAR / ARRAY /
 * DIMORDINATE / GROUP / MATCHPROP.
 */

import {
    runInteraction, PT, PN, PLoopK, PTxt, PK, requireSelection, entityToPolyline, makeEntity,
    rubberBand, curveEntity,
} from './lib';
import type { RunnerMap } from '../runnerRegistry';
import { undoableAdd, undoableModify, undoableReplace } from '../entityOps';
import { useAppStore, type Entity } from '../../../state/store';
import { useSystemVarsStore } from '../../../state/useSystemVarsStore';
import { triangulatePolygon, centroid } from '../geometryMath';
import type { Pt3 } from '../geometryMath';
import { cloneEntityData, translateData } from '../../../tools/cad/entityGeometry';
import { generatePatHatchSegments } from '../../../utils/hatchPat';
import { resolveHatchPattern } from '../../../state/useHatchPatternStore';
import { notify } from '../../../utils/notify';
import i18n from '../../../i18n';

// ---------------------------------------------------------------------------
// shared helpers
// ---------------------------------------------------------------------------

/** Clip a polygon boundary into hatch pattern segments (parallel lines at
 *  `angle` rad, spacing `scale`, plus optional cross-hatch for ANSI37).
 *  Sample-based: each grid line is sampled and inside/outside runs are
 *  collected — robust for concave boundaries. */
export function hatchSegments(boundary: Pt3[], angle: number, spacing: number, cross = false): number[][] {
    const segs: number[][] = [];
    if (boundary.length < 3) return segs;
    const cx = centroid(boundary);
    const inside = (p: Pt3) => pointInPolygon(p, boundary);

    const pass = (rot: number) => {
        const cs = Math.cos(rot), sn = Math.sin(rot);
        // Rotate boundary into the pattern frame so lines are horizontal.
        const local = boundary.map((p) => {
            const dx = p[0] - cx[0], dy = p[1] - cx[1];
            return [dx * cs + dy * sn, -dx * sn + dy * cs, p[2] ?? 0] as Pt3;
        });
        const ys = local.map((p) => p[1]);
        const y0 = Math.floor(Math.min(...ys) / spacing) * spacing;
        const y1 = Math.ceil(Math.max(...ys) / spacing) * spacing;
        const xLo = Math.min(...local.map((p) => p[0])) - 1, xHi = Math.max(...local.map((p) => p[0])) + 1;
        const steps = Math.max(16, Math.round((xHi - xLo) / (spacing * 0.25)));
        for (let y = y0; y <= y1; y += spacing) {
            let runStart: number | null = null;
            for (let i = 0; i <= steps; i++) {
                const x = xLo + ((xHi - xLo) * i) / steps;
                const lp: Pt3 = [x, y, 0];
                // Rotate the sample back to world for the inside test.
                const wp: Pt3 = [lp[0] * cs - lp[1] * sn + cx[0], lp[0] * sn + lp[1] * cs + cx[1], cx[2]];
                const isOn = inside(wp);
                if (isOn && runStart === null) runStart = x;
                if ((!isOn || i === steps) && runStart !== null) {
                    const a = [runStart, y, 0], b = [x, y, 0];
                    const wa: Pt3 = [a[0] * cs - a[1] * sn + cx[0], a[0] * sn + a[1] * cs + cx[1], cx[2]];
                    const wb: Pt3 = [b[0] * cs - b[1] * sn + cx[0], b[0] * sn + b[1] * cs + cx[1], cx[2]];
                    if (Math.hypot(wb[0] - wa[0], wb[1] - wa[1]) > spacing * 0.1) {
                        segs.push([wa[0], wa[1], wa[2], wb[0], wb[1], wb[2]]);
                    }
                    runStart = null;
                }
            }
        }
    };
    pass(angle);
    if (cross) pass(angle + Math.PI / 2);
    return segs;
}

function pointInPolygon(p: Pt3, poly: Pt3[]): boolean {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i][0], yi = poly[i][1], xj = poly[j][0], yj = poly[j][1];
        const intersect = ((yi > p[1]) !== (yj > p[1]))
            && (p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi + 1e-12) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
}

/** Duplicate an entity by data transform; returns the copy (not added). */
function duplicatedEntity(src: Entity, mutate: (data: Record<string, any>) => void, nameSuffix: string): Entity {
    const data = cloneEntityData(src.data);
    mutate(data);
    return {
        id: `cad:${crypto.randomUUID()}`,
        type: src.type,
        name: `${src.name} ${nameSuffix}`,
        visible: true,
        locked: false,
        layer: src.layer,
        data,
    };
}

// ---------------------------------------------------------------------------
// runners
// ---------------------------------------------------------------------------

export const cadDraftRunners: RunnerMap = {
    // HATCH: pick boundary objects (or draw an interior point loop first if
    // nothing is pickable), then pattern/scale/angle. Produces a real 'hatch'
    // entity whose pattern lines are computed at creation (FILLMODE aware).
    'draw.hatch': (args) => {
        const pattern = (args?.[0] ?? 'ANSI31').toUpperCase();
        runInteraction({
            steps: [
                PLoopK('HATCH: pick boundary objects (Enter when done)', 1),
                PN('HATCH: pattern scale <0.5>'),
                PN('HATCH: pattern angle (deg) <0>'),
            ],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const loops = picks
                    .map((id) => entityToPolyline(entities[id]))
                    .filter((poly): poly is Pt3[] => !!poly && poly.length >= 3);
                if (loops.length === 0) return 'HATCH: no closed boundary among picks.';
                const scale = Math.max(0.02, Number(values[1]) || 0.5);
                const angle = ((Number(values[2]) || 0) * Math.PI) / 180;
                const fillmodeOn = (useSystemVarsStore.getState().FILLMODE ?? 1) !== 0;
                const cross = pattern === 'ANSI37' || pattern === 'CROSS';
                const solid = pattern === 'SOLID';
                // Imported/built-in .pat definitions drive the pattern; the
                // simple parallel generator only covers unresolvable names.
                const patDef = resolveHatchPattern(pattern);
                const created = loops.map((loop) => {
                    const closed = [...loop];
                    if (Math.hypot(closed[0][0] - closed[closed.length - 1][0], closed[0][1] - closed[closed.length - 1][1]) > 1e-6) {
                        closed.push([...closed[0]] as Pt3);
                    }
                    const segments = solid || !fillmodeOn ? [] : patDef
                        ? generatePatHatchSegments(closed, patDef, { scale, rotationDeg: (angle * 180) / Math.PI })
                        : hatchSegments(closed, angle, scale, cross);
                    return makeEntity('hatch', `Hatch-${Date.now()}`, {
                        boundary: closed,
                        segments,
                        pattern: solid ? 'SOLID' : pattern,
                        scale,
                        angle,
                        fill: solid,
                    });
                });
                undoableAdd(created);
                return `HATCH: ${created.length} region(s) filled (${solid ? 'solid' : pattern}, spacing ${scale}).`;
            },
        });
        return `HATCH (${pattern}): pick boundary objects`;
    },

    // HATCHEDIT: full edit — pattern / spacing / angle / color on a picked hatch.
    'draw.hatchEdit': () => {
        runInteraction({
            steps: [
                PK('HATCHEDIT: pick a hatch'),
                PTxt('HATCHEDIT: pattern name (ANSI31/ANSI37/SOLID or imported .pat)'),
                PN('HATCHEDIT: spacing <0.5>'),
                PN('HATCHEDIT: angle (deg) <0>'),
                PTxt('HATCHEDIT: color (hex or "keep")'),
            ],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e || e.type !== 'hatch') return 'HATCHEDIT: pick a hatch object.';
                const pattern = String(values[1] ?? 'ANSI31').trim().toUpperCase() || 'ANSI31';
                const scale = Math.max(0.02, Number(values[2]) || 0.5);
                const angle = ((Number(values[3]) || 0) * Math.PI) / 180;
                const color = String(values[4] ?? 'keep').trim();
                const patDef = resolveHatchPattern(pattern);
                undoableModify([picks[0]], (d) => {
                    const boundary = (d.boundary as number[][]) as Pt3[];
                    const solid = pattern === 'SOLID';
                    const fillmodeOn = (useSystemVarsStore.getState().FILLMODE ?? 1) !== 0;
                    return {
                        ...d,
                        pattern: solid ? 'SOLID' : pattern,
                        scale,
                        angle,
                        color: color && color !== 'keep' ? color : d.color,
                        segments: solid || !fillmodeOn ? [] : patDef
                            ? generatePatHatchSegments(boundary, patDef, { scale, rotationDeg: (angle * 180) / Math.PI })
                            : hatchSegments(boundary, angle, scale, pattern === 'ANSI37'),
                    };
                });
                return `HATCHEDIT: pattern ${pattern}, spacing ${scale}.`;
            },
        });
        return 'HATCHEDIT: pick a hatch';
    },

    // REGION: closed boundary objects -> planar region solids (CAD ledger).
    'draw.region': () => {
        runInteraction({
            steps: [PLoopK('REGION: pick closed curves (Enter when done)', 1)],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const created: Entity[] = [];
                picks.forEach((id) => {
                    const poly = entityToPolyline(entities[id]);
                    if (!poly || poly.length < 3) return;
                    const verts = poly.map((p) => [p[0], p[1], p[2] ?? 0]);
                    const idx = triangulatePolygon(verts as Pt3[]);
                    created.push(makeEntity('solid', `Region-${Date.now()}`, {
                        vertices: verts, indices: idx, color: '#9aa7b0', region: true, flat: true,
                    }));
                });
                if (created.length === 0) return 'REGION: no closed curve among picks.';
                undoableAdd(created);
                return `REGION: ${created.length} region(s) created.`;
            },
        });
        return 'REGION: pick closed curves';
    },

    // REVCLOUD: two corners -> scalloped arc-chord cloud polyline.
    'draw.revisionCloud': () => {
        runInteraction({
            steps: [PT('REVCLOUD: first corner'), PT('REVCLOUD: opposite corner')],
            preview: rubberBand('#ffff00'),
            complete: (pts) => {
                const a = pts[0], b = pts[1];
                const x0 = Math.min(a.x, b.x), x1 = Math.max(a.x, b.x);
                const y0 = Math.min(a.y, b.y), y1 = Math.max(a.y, b.y);
                if (x1 - x0 < 1e-4 || y1 - y0 < 1e-4) return 'REVCLOUD: window too small.';
                const w = x1 - x0, h = y1 - y0;
                const arcLen = Math.max(0.4, Math.min(w, h) / 6);
                const corners: Pt3[] = [[x0, y0, a.z], [x1, y0, a.z], [x1, y1, a.z], [x0, y1, a.z]];
                const outline: Pt3[] = [];
                for (let c = 0; c < 4; c++) {
                    const p = corners[c], q = corners[(c + 1) % 4];
                    const len = Math.hypot(q[0] - p[0], q[1] - p[1]);
                    const n = Math.max(2, Math.round(len / arcLen));
                    for (let i = 0; i < n; i++) {
                        const t = i / n;
                        const x = p[0] + (q[0] - p[0]) * t;
                        const y = p[1] + (q[1] - p[1]) * t;
                        // Alternate bulge outward on each chord for the scallop.
                        const dirx = (q[0] - p[0]) / len, diry = (q[1] - p[1]) / len;
                        const outx = diry * -1, outy = dirx;
                        const bulge = (i % 2 === 0 ? -1 : 1) * 0.08 * len * (arcLen / len);
                        const tm = t + 0.5 / n;
                        const mx = p[0] + (q[0] - p[0]) * tm - outx * bulge;
                        const my = p[1] + (q[1] - p[1]) * tm - outy * bulge;
                        outline.push([x, y, p[2]]);
                        outline.push([mx, my, p[2]]);
                    }
                }
                outline.push([...outline[0]] as Pt3);
                undoableAdd([curveEntity(outline, `RevCloud-${Date.now()}`, { closed: true })]);
                return 'Revision cloud created.';
            },
        });
        return 'REVCLOUD: specify first corner';
    },

    // ARRAYRECT: pick count + spacing points like 2D. All produced
    // objects (source included) are tagged as an editable array group so
    // ARRAYEDIT can regenerate the grid from stored parameters.
    'arr.rect': () => {
        runInteraction({
            steps: [
                PN('ARRAYRECT: number of rows <3>'),
                PN('ARRAYRECT: number of columns <3>'),
                PT('ARRAYRECT: specify spacing between columns (first point)'),
                PT('ARRAYRECT: specify spacing between columns (second point)'),
                PT('ARRAYRECT: specify spacing between rows (first point)'),
                PT('ARRAYRECT: specify spacing between rows (second point)'),
            ],
            complete: (pts, values) => {
                const list = requireSelection('ARRAYRECT');
                if (!list) return 'ARRAYRECT: select objects first.';
                const rows = Math.max(1, Math.round(Number(values[0]) || 3));
                const cols = Math.max(1, Math.round(Number(values[1]) || 3));
                const dcol: [number, number] = [pts[3].x - pts[2].x, pts[3].y - pts[2].y];
                const drow: [number, number] = [pts[5].x - pts[4].x, pts[5].y - pts[4].y];
                const arrayId = `arr:${crypto.randomUUID().slice(0, 8)}`;
                const params = { kind: 'rect', rows, cols, dcol, drow };
                const created: Entity[] = [];
                for (let r = 0; r < rows; r++) {
                    for (let c = 0; c < cols; c++) {
                        list.forEach((src) => {
                            const isSource = r === 0 && c === 0;
                            if (isSource) {
                                // Tag the originals in place; every member
                                // carries the regeneration template.
                                created.push({ ...src, data: { ...cloneEntityData(src.data), arrayId, array: params } });
                            } else {
                                created.push(duplicatedEntity(src, (d) => {
                                    const next = translateData(d, [dcol[0] * c + drow[0] * r, dcol[1] * c + drow[1] * r, 0]);
                                    Object.assign(d, next);
                                    d.arrayId = arrayId;
                                    d.array = params;
                                }, 'Array'));
                            }
                        });
                    }
                }
                // Replace the selection (sources) with the full tagged grid.
                undoableReplace(list.map((e) => e.id), created);
                return `ARRAYRECT: ${cols}x${rows} grid created (${created.length} objects, ARRAYEDIT to change).`;
            },
        });
        return 'ARRAYRECT: select objects, then specify rows';
    },

    // ARRAYPOLAR: center + angle + count around a base point. Members are
    // tagged with the array parameters so ARRAYEDIT can regenerate.
    'arr.polar': () => {
        runInteraction({
            steps: [
                PN('ARRAYPOLAR: number of items <4>'),
                PT('ARRAYPOLAR: center point'),
                PT('ARRAYPOLAR: angle endpoint (defines total arc, 360 for full)'),
            ],
            complete: (pts, values) => {
                const list = requireSelection('ARRAYPOLAR');
                if (!list) return 'ARRAYPOLAR: select objects first.';
                const count = Math.max(2, Math.round(Number(values[0]) || 4));
                const c = pts[0];
                const total = Math.atan2(pts[1].y - c.y, pts[1].x - c.x);
                const arrayId = `arr:${crypto.randomUUID().slice(0, 8)}`;
                const params = { kind: 'polar', count, center: [c.x, c.y, c.z] as Pt3, total };
                const created: Entity[] = [];
                list.forEach((src) => {
                    created.push({ ...src, data: { ...cloneEntityData(src.data), arrayId, array: params } });
                });
                for (let i = 1; i < count; i++) {
                    const ang = (total * i) / count;
                    const cos = Math.cos(ang), sin = Math.sin(ang);
                    list.forEach((src) => {
                        created.push(duplicatedEntity(src, (d) => {
                            const rotate = (p: number[]): number[] => {
                                const dx = (p[0] ?? 0) - c.x, dy = (p[1] ?? 0) - c.y;
                                return [c.x + dx * cos - dy * sin, c.y + dx * sin + dy * cos, p[2] ?? 0];
                            };
                            const next = transformBagPoints(cloneEntityData(d), rotate);
                            Object.assign(d, next);
                            d.arrayId = arrayId;
                            d.array = params;
                        }, 'PolarArray'));
                    });
                }
                undoableReplace(list.map((e) => e.id), created);
                return `ARRAYPOLAR: ${count} items over ${(total * 180 / Math.PI).toFixed(0)}° (${created.length} objects, ARRAYEDIT to change).`;
            },
        });
        return 'ARRAYPOLAR: select objects, specify items then center';
    },

    // ARRAYEDIT: pick any array member, re-run its generator with new
    // parameters. Regenerates the whole group (delete all members, rebuild).
    'arr.edit': () => {
        runInteraction({
            steps: [PK('ARRAYEDIT: pick an array member'), PN('ARRAYEDIT: new item count/rows <3>')],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e) return 'ARRAYEDIT: invalid pick.';
                const params = (e.data as Record<string, any>).array as {
                    kind?: string; rows?: number; cols?: number; count?: number;
                    dcol?: [number, number]; drow?: [number, number];
                    center?: Pt3; total?: number;
                } | undefined;
                if (!params?.kind) return 'ARRAYEDIT: the picked object is not part of an editable array (create one with ARRAYRECT/ARRAYPOLAR).';
                const arrayId = String((e.data as Record<string, any>).arrayId ?? '');
                const members = Object.values(entities).filter((x) => (x.data as Record<string, any>).arrayId === arrayId);
                if (members.length === 0) return 'ARRAYEDIT: array group is empty.';
                // Rebuild from the source member (the un-offset one at index 0
                // of the original insert order): its data bag is the template.
                const template = structuredClone(members[0].data) as Record<string, any>;
                delete template.arrayId;
                delete template.array;
                const srcLike: Entity = { ...members[0], data: template };
                const n = Math.max(2, Math.round(Number(values[1]) || 3));
                const created: Entity[] = [];
                if (params.kind === 'rect' && params.dcol && params.drow) {
                    const rows = n, cols = Math.max(1, params.cols ?? n);
                    const grp = { ...params, rows };
                    for (let r = 0; r < rows; r++) {
                        for (let cc = 0; cc < cols; cc++) {
                            if (r === 0 && cc === 0) {
                                created.push({ ...srcLike, data: { ...template, arrayId, array: grp } });
                            } else {
                                created.push(duplicatedEntity(srcLike, (d) => {
                                    Object.assign(d, translateData(d, [params.dcol![0] * cc + params.drow![0] * r, params.dcol![1] * cc + params.drow![1] * r, 0]));
                                    d.arrayId = arrayId; d.array = grp;
                                }, 'Array'));
                            }
                        }
                    }
                    undoableReplace(members.map((m) => m.id), created);
                    return `ARRAYEDIT: rectangular array regenerated at ${cols}x${rows}.`;
                }
                if (params.kind === 'polar' && params.center && params.total !== undefined) {
                    const grp = { ...params, count: n };
                    created.push({ ...srcLike, data: { ...template, arrayId, array: grp } });
                    for (let i = 1; i < n; i++) {
                        const ang = ((grp.total ?? 0) * i) / n;
                        const cos = Math.cos(ang), sin = Math.sin(ang);
                        created.push(duplicatedEntity(srcLike, (d) => {
                            const cx = grp.center![0], cy = grp.center![1];
                            const rot = (p: number[]): number[] => {
                                const dx = (p[0] ?? 0) - cx, dy = (p[1] ?? 0) - cy;
                                return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos, p[2] ?? 0];
                            };
                            Object.assign(d, transformBagPoints(cloneEntityData(d), rot));
                            d.arrayId = arrayId; d.array = grp;
                        }, 'PolarArray'));
                    }
                    undoableReplace(members.map((m) => m.id), created);
                    return `ARRAYEDIT: polar array regenerated at ${n} items.`;
                }
                return 'ARRAYEDIT: unknown array kind.';
            },
        });
        return 'ARRAYEDIT: pick an array member';
    },

    // ARRAY wizard: TYPE arg (rect/polar) or defaults to rectangular.
    'arr.general': (args) => {
        const kind = (args?.[0] ?? 'rect').toLowerCase();
        const hit = kind.startsWith('pol') ? cadDraftRunners['arr.polar'] : cadDraftRunners['arr.rect'];
        notify(i18n.t('tools.notify.arrayRunningTheRectangularGridFlow'), 'info');
        return hit ? hit([]) : 'ARRAY: unavailable.';
    },

    // DIMORDINATE: feature point + axis choice from the command args (X/Y).
    'dim.ordinate': (args) => {
        const axis = (args?.[0] ?? 'y').toLowerCase().startsWith('x') ? 'x' : 'y';
        runInteraction({
            steps: [
                PT(`DIMORDINATE: feature point (axis ${axis.toUpperCase()})`),
                PT('DIMORDINATE: leader endpoint (label side)'),
            ],
            complete: (pts) => {
                const f = pts[0], l = pts[1];
                const value = (axis === 'x' ? f.x : f.y).toFixed(2);
                undoableAdd([makeEntity('dimension', `Dim-ordinate-${Date.now()}`, {
                    type: 'ordinate',
                    axis,
                    point1: [f.x, f.y, f.z],
                    point2: [l.x, l.y, l.z],
                    textPosition: [l.x, l.y, l.z],
                    override: value,
                })]);
                return `Ordinate dimension ${axis.toUpperCase()}=${value} added.`;
            },
        });
        return `DIMORDINATE: specify feature point (${axis.toUpperCase()} axis)`;
    },

    // GROUP: tag the selection with a shared group id. Clicking any member
    // selects the whole group (SceneContent expansion), UNGROUP clears it.
    'sel.group': (args) => {
        const name = args?.join(' ')?.trim() || `A${Date.now() % 100000}`;
        const list = requireSelection('GROUP');
        if (!list) return 'GROUP: select objects first.';
        const ids = list.map((e) => e.id);
        undoableModify(ids, (d) => ({ ...d, groupId: name }));
        return `GROUP "${name}": ${ids.length} object(s) grouped.`;
    },

    // MATCHPROP: pick source object, then paint targets (Enter to end).
    // Copies the appearance/style fields (color, linetype, lineweight,
    // transparency) plus the layer — the CAD property set.
    'vis.matchProp': () => {
        runInteraction({
            steps: [
                PK('MATCHPROP: pick the source object'),
                PLoopK('MATCHPROP: pick target objects (Enter to finish)', 1),
            ],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const src = entities[picks[0]];
                if (!src) return 'MATCHPROP: invalid source pick.';
                const targets = picks.slice(1);
                if (targets.length === 0) return 'MATCHPROP: no targets selected.';
                const d = src.data as Record<string, unknown>;
                const style = {
                    color: d.color,
                    linetype: d.linetype,
                    lineWeight: d.lineWeight,
                    transparency: d.transparency,
                };
                undoableModify(targets, (td) => {
                    const next = { ...td };
                    Object.entries(style).forEach(([k, v]) => {
                        if (v === undefined) delete next[k];
                        else next[k] = v;
                    });
                    return next;
                });
                targets.forEach((id) => {
                    if (entities[id]) useAppStore.getState().updateEntity(id, { layer: src.layer });
                });
                return `MATCHPROP: style painted onto ${targets.length} object(s).`;
            },
        });
        return 'MATCHPROP: pick source object';
    },
};

// ---------------------------------------------------------------------------

/** Apply a point function to every point-bearing field of a data bag. */
function transformBagPoints(data: Record<string, any>, fn: (p: number[]) => number[]): Record<string, any> {
    ['start', 'end', 'position', 'center'].forEach((k) => {
        if (Array.isArray(data[k]) && typeof data[k][0] === 'number') data[k] = fn(data[k]);
    });
    ['vertices', 'controlPoints'].forEach((k) => {
        if (Array.isArray(data[k])) data[k] = data[k].map((p: number[]) => (Array.isArray(p) ? fn(p) : p));
    });
    return data;
}
